import React from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import {
  LocalShipping as LocalShippingIcon,
  LocationOn as LocationOnIcon,
} from '@material-ui/icons'

import {
  useQueryPaginator,
  DetailTable,
  LinkedField,
  NumberField,
  PaginatedTable,
  ValueField,
} from 'src/components/Table'

import { skuit } from 'src/services/skuit'
import { Pallet, PalletASN } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  pallet: UseQueryResult<Pallet>
}

const Details = (props: DetailsProps) => {
  const { pallet } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocationOnIcon />
          <Typography variant="h6" gutterBottom>
            {pallet && pallet.data?.name}
          </Typography>
          <DetailTable
            obj={pallet}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'name', label: 'Name', renderer: ValueField },
              { id: 'clientID', label: 'Your ID', renderer: ValueField },
              { id: 'totalUnits', label: 'Total Unit Count', renderer: NumberField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface ASNDetailsProps {
  asn: UseQueryResult<PalletASN>
}

const ASNDetails = (props: ASNDetailsProps) => {
  const { asn } = props
  return (
    <Card mb={6}>
      <CardContent>
        <Centered>
          <LocalShippingIcon />
          <Typography variant="h6" gutterBottom>
            Advanced Shipping Notice
          </Typography>
          <DetailTable
            obj={asn}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              { id: 'billOfLadingNumber', label: 'Bill of Lading Number', renderer: ValueField },
            ]}
            customFields={asn.data?.customFields}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const PalletPage = () => {
  const { guid } = useParams<PathParams>()
  const pallet = useQuery<Pallet>(['pallet'], () => skuit.client.pallets.retrieve(guid))

  const skusPaginator = useQueryPaginator('pallet-skus', skuit.client.skus.list, {
    params: { page_size: 5, pallets: guid },
  })
  const unitsPaginator = useQueryPaginator('pallet-units', skuit.client.units.list, {
    params: { page_size: 5, containers: guid },
  })
  const barcodedPaginator = useQueryPaginator(
    'pallet-barcoded-units',
    skuit.client.barcodedUnits.list,
    {
      params: { page_size: 5, containers: guid },
    }
  )

  return (
    <>
      <Helmet title="Pallet" />

      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Pallet
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/pallets">
              Pallets
            </Link>
            <Typography>{pallet && pallet.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              <Details pallet={pallet} />
            </Grid>
          </Grid>
        </Grid>
        {!!pallet.data?.asn && (
          <Grid item xs={12} lg={6}>
            <ASNDetails asn={{ ...pallet, data: pallet.data.asn }} />
          </Grid>
        )}
        <Grid item xs={12}>
          <PaginatedTable
            title="SKUs"
            fields={[{ id: 'sku', label: 'Name', renderer: ValueField }]}
            paginator={skusPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units"
            fields={[
              { id: 'product.name', label: 'Product', renderer: ValueField },
              { id: 'sku.sku', label: 'SKU', renderer: ValueField },
              { id: 'numUnits', label: 'Number of Units', renderer: ValueField },
            ]}
            paginator={unitsPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Barcoded Units"
            fields={[
              { id: 'guid', label: 'Name', renderer: LinkedField(ValueField) },
              { id: 'product.name', label: 'Product', renderer: ValueField },
            ]}
            paginator={barcodedPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
